export type BreathLevel = 'low' | 'moderate' | 'high' | 'halitophobia-watch';

export type BreathSource =
  | 'tongue-coating'
  | 'dry-mouth'
  | 'reflux'
  | 'gum-bleeding'
  | 'tonsil-stones'
  | 'sinus'
  | 'diet-coffee'
  | 'unknown';

export type BreathEvidence =
  | 'self-report'
  | 'photo'
  | 'partner-feedback'
  | 'dentist-report'
  | 'medical-report'
  | 'routine-response'
  | 'avoidance-pattern';

export interface BreathProfile {
  readonly level: BreathLevel;
  readonly anxiety: number;
  readonly odorConcern: number;
  readonly socialAvoidance: number;
  readonly sources: readonly BreathSource[];
  readonly symptoms: readonly string[];
  readonly evidence: readonly BreathEvidence[];
  readonly evidenceAttachments: readonly BreathEvidenceAttachment[];
  readonly goal: string;
  readonly notes: string;
  readonly nextAction: string;
}

export interface BreathEvidenceAttachment {
  readonly key: string;
  readonly filename: string;
  readonly contentType: string;
  readonly size: number;
  readonly evidenceType: string;
  readonly uploadedAt: string;
  readonly downloadPath: string;
}

export const SOURCE_LABELS: Record<BreathSource, string> = {
  'tongue-coating': 'Tongue coating',
  'dry-mouth': 'Dry mouth',
  reflux: 'Reflux / acid taste',
  'gum-bleeding': 'Gum bleeding',
  'tonsil-stones': 'Tonsil stones',
  sinus: 'Sinus / post-nasal drip',
  'diet-coffee': 'Coffee / diet triggers',
  unknown: 'Unknown',
};

export const EVIDENCE_LABELS: Record<BreathEvidence, string> = {
  'self-report': 'Self report',
  photo: 'Mouth/tongue photo',
  'partner-feedback': 'Trusted feedback',
  'dentist-report': 'Dentist report',
  'medical-report': 'Medical report',
  'routine-response': 'Routine response',
  'avoidance-pattern': 'Avoidance/checking pattern',
};

export const LEVEL_COPY: Record<BreathLevel, { label: string; summary: string }> = {
  low: {
    label: 'Low / situational',
    summary: 'Focus on trigger awareness and light prep before higher-stakes moments.',
  },
  moderate: {
    label: 'Moderate / recurring',
    summary: 'Use a consistent oral-care and hydration regime while tracking likely sources.',
  },
  high: {
    label: 'High / persistent',
    summary:
      'Track aggressively and consider dental, ENT, or reflux evaluation if patterns persist.',
  },
  'halitophobia-watch': {
    label: 'Halitophobia watch',
    summary:
      'Concern and checking look stronger than available evidence; use evidence limits and avoid reassurance loops.',
  },
};

export const DEFAULT_BREATH_PROFILE: BreathProfile = {
  level: 'moderate',
  anxiety: 5,
  odorConcern: 5,
  socialAvoidance: 4,
  sources: ['tongue-coating', 'dry-mouth'],
  symptoms: ['morning breath', 'dry mouth'],
  evidence: ['self-report'],
  evidenceAttachments: [],
  goal: 'Feel prepared before close-contact conversations',
  notes: '',
  nextAction: 'Run a 7-day tongue-cleaning, hydration, and confidence tracking baseline.',
};

function asNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, Math.min(10, Math.round(value)))
    : fallback;
}

export function scoreBreathLevel(input: {
  readonly odorConcern: number;
  readonly anxiety: number;
  readonly socialAvoidance: number;
  readonly evidence: readonly BreathEvidence[];
}): BreathLevel {
  const evidenceStrength = input.evidence.filter((item) => item !== 'self-report').length;
  if (input.anxiety >= 8 && input.odorConcern <= 4 && evidenceStrength === 0) {
    return 'halitophobia-watch';
  }
  if (input.odorConcern >= 8 || (input.odorConcern >= 6 && input.socialAvoidance >= 7)) {
    return 'high';
  }
  if (input.odorConcern >= 4 || input.socialAvoidance >= 4) return 'moderate';
  return 'low';
}

export function nextActionForProfile(profile: Pick<BreathProfile, 'level' | 'sources'>): string {
  if (profile.level === 'halitophobia-watch') {
    return 'Use one evidence check per close-contact moment, then stop repeated checking.';
  }
  if (profile.sources.includes('gum-bleeding')) {
    return 'Prioritize gum bleeding tracking and schedule dental review if it persists.';
  }
  if (profile.sources.includes('reflux')) {
    return 'Run a reflux-trigger log around meals, late eating, coffee, and acid taste.';
  }
  if (profile.sources.includes('sinus')) {
    return 'Track post-nasal drip, congestion, and morning breath as one cluster.';
  }
  return 'Run a 7-day tongue-cleaning, hydration, and confidence tracking baseline.';
}

export function parseBreathProfilePayload(
  payload: Record<string, unknown> | null | undefined,
): BreathProfile {
  if (!payload) return DEFAULT_BREATH_PROFILE;
  const sources = Array.isArray(payload.sources)
    ? payload.sources.filter(
        (source): source is BreathSource => typeof source === 'string' && source in SOURCE_LABELS,
      )
    : DEFAULT_BREATH_PROFILE.sources;
  const evidence = Array.isArray(payload.evidence)
    ? payload.evidence.filter(
        (item): item is BreathEvidence => typeof item === 'string' && item in EVIDENCE_LABELS,
      )
    : DEFAULT_BREATH_PROFILE.evidence;
  const evidenceAttachments = Array.isArray(payload.evidenceAttachments)
    ? payload.evidenceAttachments
        .map((item): BreathEvidenceAttachment | null => {
          if (!item || typeof item !== 'object') return null;
          const record = item as Record<string, unknown>;
          if (typeof record.key !== 'string' || typeof record.filename !== 'string') return null;
          return {
            key: record.key,
            filename: record.filename,
            contentType:
              typeof record.contentType === 'string'
                ? record.contentType
                : 'application/octet-stream',
            size: typeof record.size === 'number' ? record.size : 0,
            evidenceType:
              typeof record.evidenceType === 'string' ? record.evidenceType : 'self-report',
            uploadedAt: typeof record.uploadedAt === 'string' ? record.uploadedAt : '',
            downloadPath: typeof record.downloadPath === 'string' ? record.downloadPath : '',
          };
        })
        .filter((item): item is BreathEvidenceAttachment => item !== null)
    : DEFAULT_BREATH_PROFILE.evidenceAttachments;
  const odorConcern = asNumber(
    payload.odorConcern ?? payload.severity,
    DEFAULT_BREATH_PROFILE.odorConcern,
  );
  const anxiety = asNumber(payload.anxiety, DEFAULT_BREATH_PROFILE.anxiety);
  const socialAvoidance = asNumber(payload.socialAvoidance, DEFAULT_BREATH_PROFILE.socialAvoidance);
  const level =
    typeof payload.level === 'string' && payload.level in LEVEL_COPY
      ? (payload.level as BreathLevel)
      : scoreBreathLevel({ odorConcern, anxiety, socialAvoidance, evidence });
  const profile = {
    level,
    anxiety,
    odorConcern,
    socialAvoidance,
    sources,
    symptoms: Array.isArray(payload.symptoms)
      ? payload.symptoms.filter((item): item is string => typeof item === 'string')
      : DEFAULT_BREATH_PROFILE.symptoms,
    evidence,
    evidenceAttachments,
    goal:
      typeof payload.goal === 'string' && payload.goal.trim()
        ? payload.goal.trim()
        : DEFAULT_BREATH_PROFILE.goal,
    notes: typeof payload.notes === 'string' ? payload.notes : '',
    nextAction: typeof payload.nextAction === 'string' ? payload.nextAction : '',
  };
  return { ...profile, nextAction: profile.nextAction || nextActionForProfile(profile) };
}

export function regimeForProfile(profile: BreathProfile): readonly string[] {
  const steps = new Set<string>();
  steps.add('Brush 2 minutes twice daily and clean the tongue gently once daily.');
  steps.add('Log odor concern, confidence, and social avoidance before one close-contact moment.');
  if (profile.sources.includes('dry-mouth'))
    steps.add('Pair coffee with water and keep a dry-mouth note when saliva feels low.');
  if (profile.sources.includes('tongue-coating'))
    steps.add('Track tongue coating before and after tongue cleaning.');
  if (profile.sources.includes('reflux'))
    steps.add('Mark reflux or acid taste after meals and avoid late trigger meals during trials.');
  if (profile.sources.includes('gum-bleeding'))
    steps.add('Track gum bleeding and flossing; dental review matters if bleeding persists.');
  if (profile.sources.includes('tonsil-stones'))
    steps.add('Record tonsil-stone signs separately from oral hygiene response.');
  if (profile.sources.includes('sinus'))
    steps.add('Track post-nasal drip, congestion, and morning breath together.');
  if (profile.anxiety >= 8 || profile.level === 'halitophobia-watch')
    steps.add('Use one evidence check, then stop repeated reassurance checking for that moment.');
  return [...steps].slice(0, 6);
}

export function weeklyPlanForProfile(profile: BreathProfile): readonly string[] {
  const plan = [
    'Day 1: capture baseline odor concern, anxiety, confidence, tongue coating, hydration, and close-contact plan.',
    'Day 2-3: keep brushing/tongue-cleaning stable; change only hydration and coffee pairing.',
    'Day 4: review whether dry mouth, reflux, sinus, or tongue coating moved with symptoms.',
    'Day 5-6: run one source-specific trial based on the strongest signal.',
    'Day 7: compare evidence against anxiety/checking and pick next week focus.',
  ];
  if (profile.level === 'halitophobia-watch') {
    return [
      'Day 1: define one evidence check only; no repeated reassurance checking after that.',
      'Day 2-3: log anxiety, checking urge, and avoidance separately from odor concern.',
      'Day 4: ask for one trusted feedback point only if appropriate.',
      'Day 5-6: practice close-contact exposure with normal hygiene, not extra checking.',
      'Day 7: compare avoided moments against actual evidence before changing hygiene.',
    ];
  }
  if (profile.sources.includes('gum-bleeding')) {
    return [
      'This week: prioritize gum bleeding notes and dental follow-up if bleeding persists.',
      ...plan.slice(1),
    ];
  }
  if (profile.sources.includes('reflux')) {
    return [
      'This week: track late meals, coffee, acid taste, and reflux timing before breath concern.',
      ...plan.slice(1),
    ];
  }
  return plan;
}

export function referralSignalsForProfile(profile: BreathProfile): readonly string[] {
  const signals: string[] = [];
  if (profile.sources.includes('gum-bleeding'))
    signals.push('Persistent gum bleeding should be reviewed by a dentist.');
  if (profile.sources.includes('tonsil-stones'))
    signals.push('Recurring tonsil-stone signs may need ENT/dental review.');
  if (profile.sources.includes('reflux'))
    signals.push('Frequent reflux or acid taste can justify medical review.');
  if (profile.level === 'high')
    signals.push(
      'Persistent high concern despite routine hygiene deserves professional evaluation.',
    );
  if (profile.level === 'halitophobia-watch')
    signals.push(
      'High anxiety with limited odor evidence may benefit from anxiety-focused support.',
    );
  return signals;
}

export function analyticsForProfile(profile: BreathProfile): {
  readonly evidenceStrength: number;
  readonly anxietyGap: number;
  readonly priority: string;
} {
  const evidenceStrength = Math.min(
    10,
    profile.evidence.filter((item) => item !== 'self-report').length * 2,
  );
  const anxietyGap = Math.max(0, profile.anxiety - profile.odorConcern - evidenceStrength / 3);
  const priority =
    profile.level === 'halitophobia-watch'
      ? 'Reduce checking loops and separate anxiety from odor evidence.'
      : profile.sources[0]
        ? `Test ${SOURCE_LABELS[profile.sources[0]].toLowerCase()} first.`
        : 'Collect stronger baseline evidence before changing the routine.';
  return { evidenceStrength, anxietyGap: Math.round(anxietyGap), priority };
}
